import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import {
  playNextVideo,
  setUserInteraction,
  setShowControls
} from '../../store/controlsPlugin.actions';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getSkipVideoButtonStyleObject } from './skipVideoButton.style';
import { Icon } from '../../../../common/components/icon/icon';
import { CONTROLS_ICON_TYPES } from '../../icon/iconMapper';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { ToolTip } from '../../../../common/components/toolTip/toolTip';
import { TOOLTIP_TITLES } from '../../controlsPlugin.constants';
import { TOOLTIP_POSITIONS } from '../../../../common/components/toolTip/toolTip.contants';
import { mediaDataSelector } from '../../../../common/store/selectors/mediaData.selector';
import { adsDataSelector } from '../../../../common/store/selectors/adsData.selector';
import { useHoverRef } from '../../../../common/hooks/useHoverRef';
import { getNextId, getNextTooltipId } from '../../htmlElements/htmlElementsIdGetter';
import { isDesktopMode } from '../../../../common/utils/utils';
import { css } from '../../../../common/styles/styles.utils';

const ARIA_LABEL = 'Next video';

export const SkipVideoButton: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playerMode = useSelector(playerDataSelector.playerMode);
  const videoList = useSelector(mediaDataSelector.videoList);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);

  const [skipIconRef, skipIconHovered] = useHoverRef();

  const desktopMode = isDesktopMode(playerMode);
  const showAdControls = isVastAd && adBreak;
  const disableIcon = videoList.length <= 1;
  const skipVideoToolTipPosition = playerSize === 'extraLarge' || playerSize === 'large' ? TOOLTIP_POSITIONS.TOP : TOOLTIP_POSITIONS.TOP_RIGHT;
  const mediumOrSmallSize = playerSize === 'small' || playerSize === 'medium';
  const showSkipVideoTooltip = desktopMode && !disableIcon;

  const styles = useStyles(getSkipVideoButtonStyleObject, [disableIcon, skipIconHovered, showAdControls, desktopMode, showSkipVideoTooltip]);
  const { skipVideoIconStyle, iconAndToolTipContainerStyle, skipIconClickableWrapperStyle, skipIconWrapperStyle } = styles;

  const onSkipClicked = (): void => {
    if (!disableIcon) {
      dispatch(setUserInteraction('nextVideo'));
      dispatch(playNextVideo());
    }
  };

  const onKeyDown = (event): void => {
    if (event.shiftKey && event.key === 'N' && !disableIcon) {
      dispatch(setUserInteraction('nextVideo'));
      dispatch(playNextVideo());
    }
  };

  const onSkipVideoFocus = (): void => {
    dispatch(setShowControls(true));
  };

  const iconProps = {
    id: getNextId(playerUniqueId),
    icon: CONTROLS_ICON_TYPES.SKIP_VIDEO_FILL,
    style: skipVideoIconStyle,
    onKeyDown,
    ariaLabel: ARIA_LABEL,
  };

  const SkipIconClickableWrapper: FunctionComponent = () => (
    <div
      ref={skipIconRef}
      style={skipIconClickableWrapperStyle}
    >
      <Icon
        {...iconProps}
        onClick={onSkipClicked}
      />
    </div>
  );

  const SkipIcon: FunctionComponent = () => (
    (!desktopMode && mediumOrSmallSize
      ? <SkipIconClickableWrapper />
      : (
        <div ref={skipIconRef} style={css(skipIconWrapperStyle)}>
          <Icon
            {...iconProps}
            onClick={onSkipClicked}
            onControlFocus={onSkipVideoFocus}
          />
        </div>
      ))
  );

  return (
    showSkipVideoTooltip
      ? (
        <div style={iconAndToolTipContainerStyle}>
          <ToolTip
            id={getNextTooltipId(playerUniqueId)}
            content={TOOLTIP_TITLES.NEXT_VIDEO}
            position={skipVideoToolTipPosition}
          >
            <SkipIcon />
          </ToolTip>
        </div>
      )
      : (
        <SkipIcon />
      )
  );
};
